import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import {
    Button,
    EmptyState,
    ErrorState,
    Input,
    SectionCard,
    useDebouncedSearch,
    useToast,
    type ColumnConfig,
    type DropdownMenuItem,
    type StatusConfig,
} from '@/components/ui';
// EAA-1878: this tab renders through the SAME accounts chrome the realm and org
// account screens use (`accounts/shared/`), not a bare `DataTable`. The shared
// component was built for exactly this — its own docstring says column CONTENT
// stays context-owned while the chrome around it is shared — so adopting it
// gives this tab selection checkboxes, the same sort-caret affordance, the same
// row kebab, the same single scroll region and the same pager as
// `/console/accounts`, instead of a second hand-tuned equivalent that drifts.
import { AccountsTable as SharedAccountsTable } from '../accounts/shared/AccountsTable';
import { AccountsPagination } from '../accounts/shared/AccountsPagination';
import { IdentityCell, StatusChip, type CellTone } from '../accounts/shared/cells';
import { isUnassignedRoleKey } from './roleKeyDisplay';
import { AppsFilterButton, AppsMenuOption } from '../apps/AppsFilterButton';
import { retryProvisioning, type ProvisioningOutcome } from '@/api/console';
import { accountDisplayName } from '../accounts/profile/accountDisplayName';
import type { AppDetailAccountRow } from './useAppDetailPage';
import type { AppDetailPageVM } from './useAppDetailPage';

// EAA-1817 (frame 53): the full outcome vocabulary provisioning.py can persist
// (see `PUSHED`/`REVOKED`/`SKIPPED_*`/`DRIFT`/`PENDING_REVOKE`/`ERROR` in
// portal-api's `directory/provisioning.py`) — `never_attempted` is the
// client-side default `outcomes_for_org` substitutes for a null `last_outcome`
// and is already part of `ProvisioningOutcome` (see `@/api/console`).
const OUTCOME_TONE: Record<ProvisioningOutcome, StatusConfig['tone']> = {
    pushed: 'success',
    revoked: 'default',
    no_mapping: 'warning',
    paused_mapping: 'warning',
    ambiguous_mapping: 'warning',
    drift: 'warning',
    pending_revoke: 'processing',
    error: 'error',
    never_attempted: 'default',
};

// EAA-1840 (2026-08-15 design revision): the frame's quick-filter chip row —
// exactly these 6 outcomes get a one-click chip (not `revoked`/`never_attempted`/
// `drift`, which stay reachable only through the "Kết quả" dropdown's full list).
/** EAA-1878: the 9 provisioning outcomes collapsed onto `shared/cells`' 3-tone
 *  vocabulary, the same way the realm screen maps enabled/pending/disabled and
 *  the org screen maps ok/pending/sync-error/revoked. The mapping is domain
 *  logic and stays here; only the dot+label widget is shared. `error` is `warn`,
 *  not `off` — `off` is the muted "nothing happened here" tone (revoked, never
 *  attempted), and a failure must not read as quieter than a stale mapping. */
const OUTCOME_CELL_TONE: Record<ProvisioningOutcome, CellTone> = {
    pushed: 'ok',
    revoked: 'off',
    never_attempted: 'off',
    no_mapping: 'warn',
    paused_mapping: 'warn',
    ambiguous_mapping: 'warn',
    drift: 'warn',
    pending_revoke: 'warn',
    error: 'warn',
};

const QUICK_FILTER_OUTCOMES: ProvisioningOutcome[] = [
    'pushed',
    'no_mapping',
    'paused_mapping',
    'ambiguous_mapping',
    'pending_revoke',
    'error',
];

// EAA-1888: outcomes that never count toward "needs attention" — matches
// `AssignmentService.outcomes_for_app`'s own `needs_attention` sum exactly,
// so the retry button's count and the facet's count can never disagree.
const HEALTHY_OUTCOMES = new Set(['pushed', 'revoked', 'never_attempted']);

/** Row shape fed to `DataTable` — the raw outcome row plus a few precomputed
 *  strings the column `render`s and the toolbar's search derive from. */
interface AccountTableRow extends AppDetailAccountRow {
    displayName: string;
    appRoleLabel: string;
}

export interface AppDetailAccountsTabProps {
    vm: AppDetailPageVM;
    /** EAA-1861: present only when the caller has resolved
     *  `console.apps.sync_users` AND the app is not internal — same
     *  fail-closed convention `AppDetailHeader`'s own `onSyncUsers` uses. */
    onSyncUsers?: () => void;
}

/**
 * EAA-1840 (2026-08-15 design revision, frame 53 "Tài khoản" tab): accounts
 * with access to this app, derived from provisioning outcomes across EVERY
 * organization — NOT a raw user list (no such list exists for an app).
 *
 * EAA-1888: this tab now reads a genuinely server-paginated/filtered/searched
 * list (`GET /admin/apps/{code}/accounts`, `useAppDetailPage`'s
 * `accountsQuery`) instead of fetching EVERY organization's full outcome list
 * and slicing it in the browser. Search/outcome/org/role filters and the
 * "Tất cả (N)" counts all come from the server now (`vm.accountsFacets`) —
 * this component has nothing left to filter or aggregate client-side beyond
 * per-row display formatting (`displayName`/`appRoleLabel`).
 *
 * Toolbar reuses `AppsFilterButton`/`AppsMenuOption` from `apps/` directly
 * (not a copy) — `RoleMapFilterButton`'s own doc explains why THAT one is a
 * deliberate copy (a different Popover mechanism, "no Ant Design" screen);
 * neither reason applies here, and `AppsFilterButton` is already a pure,
 * unstyled-by-domain presentational trigger. `DataTable`'s own generic
 * built-in toolbar (`showToolbar`) was tried first and dropped — it renders
 * a plain search box + antd `Select` filters with no live counts, not the
 * frame's "Tất cả {n}" dark-pill affordance this reuse gives for free.
 *
 * Known gaps recorded in `docs/SRS-Portal-Apps-RoleMap-Frontend.md`, not
 * papered over: (13) the persisted outcome/timestamp/retry-count/fix are
 * per-ASSIGNMENT (the worst case across every app the org's Level fans out
 * to), not per-app — a row here may describe a different app's failure when
 * the same Level also grants elsewhere. The frame's "Chi tiết vòng chạy"
 * button is omitted, not fabricated — there is no persisted "sync run"
 * record for provisioning the way there is for catalog sync, so there is
 * nothing real for it to open. EAA-1888 adds one more: the "Thử lại" button
 * now retries only the CURRENT PAGE's failing rows, not every failing row
 * for this app across every organization — that app-wide sweep needed the
 * full cross-org row list client-side, which is exactly the load this
 * ticket removes. A real cross-org bulk-retry is a follow-up, not silently
 * kept by leaving the old fetch in place.
 */
export function AppDetailAccountsTab({ vm, onSyncUsers }: AppDetailAccountsTabProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();

    const outcomeConfig = useMemo<Record<string, StatusConfig>>(() => {
        const labels: Record<ProvisioningOutcome, string> = {
            pushed: t('console.appDetail.accounts.outcome.pushed'),
            revoked: t('console.appDetail.accounts.outcome.revoked'),
            no_mapping: t('console.appDetail.accounts.outcome.noMapping'),
            paused_mapping: t('console.appDetail.accounts.outcome.pausedMapping'),
            ambiguous_mapping: t('console.appDetail.accounts.outcome.ambiguousMapping'),
            drift: t('console.appDetail.accounts.outcome.drift'),
            pending_revoke: t('console.appDetail.accounts.outcome.pendingRevoke'),
            error: t('console.appDetail.accounts.outcome.error'),
            never_attempted: t('console.appDetail.accounts.outcome.neverAttempted'),
        };
        return Object.fromEntries(
            (Object.keys(labels) as ProvisioningOutcome[]).map((key) => [
                key,
                { label: labels[key], tone: OUTCOME_TONE[key] },
            ])
        );
    }, [t]);

    // EAA-1817: reverse of `AppRoleConversion.convertedTo` (app role -> standard
    // role keys) — the design's "vai trò chuẩn → vai trò trong app" column reads
    // the other direction (standard role key -> this app's role(s)). Independent
    // of EAA-1888's server-side RELEVANCE filter: this is purely a display lookup
    // for whichever rows the server already decided to return.
    const appRolesByRoleKey = useMemo(() => {
        const map: Record<string, string[]> = {};
        vm.roleConversions.forEach((role) => {
            role.convertedTo.forEach((roleKey) => {
                map[roleKey] = [...(map[roleKey] ?? []), role.key];
            });
        });
        return map;
    }, [vm.roleConversions]);

    const rows: AccountTableRow[] = useMemo(
        () =>
            vm.accountRows.map((row) => ({
                ...row,
                displayName: accountDisplayName({
                    first_name: row.first_name,
                    last_name: row.last_name,
                    username: '',
                    email: row.user_email,
                }),
                appRoleLabel: (appRolesByRoleKey[row.role_key ?? ''] ?? []).join(', '),
            })),
        [vm.accountRows, appRolesByRoleKey]
    );

    // EAA-1888: debounced so typing a search term fires one server request per
    // pause, not one per keystroke — `vm.accountsSearch`/`setAccountsSearch`
    // now drive a real query, unlike the pre-1888 instant client-side filter.
    const { localValue: searchValue, handleChange: handleSearchChange } = useDebouncedSearch(
        vm.accountsSearch,
        vm.setAccountsSearch
    );

    const [openFilter, setOpenFilter] = useState<'outcome' | 'org' | 'role' | null>(null);
    // EAA-1878: row selection, the capability adopting `shared/AccountsTable`
    // brings. It is what makes "Thử lại" a deliberate act on chosen rows rather
    // than "whatever happens to be on this page" — see `retryTargets` below.
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

    const filtersActive = Boolean(
        vm.accountsSearch || vm.accountsOutcomeFilter || vm.accountsOrgFilter || vm.accountsRoleFilter
    );
    function clearFilters() {
        vm.setAccountsSearch('');
        vm.setAccountsOutcomeFilter(null);
        vm.setAccountsOrgFilter(null);
        vm.setAccountsRoleFilter(null);
    }

    // EAA-1888: every count below comes from `vm.accountsFacets` — computed
    // server-side over the app-relevant, visibility-scoped set, NEVER narrowed
    // by whichever filter is currently applied (see `AssignmentService.
    // outcomes_for_app`'s own docstring). `allRowsTotal` sums the outcome
    // facet (every relevant row falls into exactly one outcome), giving the
    // "Tất cả (N)" pill the same total every dropdown's own "Tất cả" option
    // shows, with no separate unfiltered fetch needed for it.
    // EAA-1878: `AccountsPagination` renders "Hiển thị {from}–{to} trong {total}"
    // and takes the range as inputs, because each adapter computes it from what
    // it actually knows server-side (see the component's own docstring).
    const rangeFrom = vm.accountsTotal === 0 ? 0 : (vm.accountsPage - 1) * vm.accountsPageSize + 1;
    const rangeTo = Math.min(vm.accountsPage * vm.accountsPageSize, vm.accountsTotal);

    const facets = vm.accountsFacets;
    // EAA-1888: `?? {}` produces a fresh object every render when `facets` is
    // absent, so the fallback itself is memoized on `facets` — otherwise
    // `allRowsTotal` below would never actually memoize while loading.
    const outcomeCounts = useMemo(() => facets?.outcome_counts ?? {}, [facets]);
    const allRowsTotal = useMemo(
        () => Object.values(outcomeCounts).reduce((sum, n) => sum + (n ?? 0), 0),
        [outcomeCounts]
    );
    const orgOptions = facets?.organizations ?? [];
    const roleOptions = useMemo(
        () =>
            (facets?.roles ?? [])
                .filter((role): role is { role_key: string; count: number } => role.role_key !== null)
                .map((role) => ({
                    key: role.role_key,
                    count: role.count,
                    // EAA-1861 / EAA-1878: the placeholder Level sentinel is not a
                    // real role key — the raw string (now `unassigned-<app code>`)
                    // would otherwise leak into this dropdown verbatim.
                    label: isUnassignedRoleKey(role.role_key)
                        ? t('console.appDetail.accounts.noRoleAssigned')
                        : role.role_key,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [facets, t]
    );

    // EAA-1878: selected rows win; with no selection this keeps the pre-existing
    // behaviour (every unhealthy row on the CURRENT PAGE — the app-wide sweep is
    // still the gap EAA-1888 recorded, not something this change quietly closes).
    const retryTargets = useMemo(() => {
        const unhealthy = rows.filter((row) => !HEALTHY_OUTCOMES.has(row.outcome));
        if (selectedKeys.length === 0) return unhealthy;
        const picked = new Set(selectedKeys);
        return unhealthy.filter((row) => picked.has(row.assignment_id));
    }, [rows, selectedKeys]);

    // EAA-1878: the shared table's row kebab. Only an UNHEALTHY row gets one —
    // retrying a pushed/revoked/never-attempted row is a no-op the backend would
    // simply not requeue, and an always-present menu item that does nothing is
    // the kind of dead affordance this console keeps out of its tables.
    const rowActions = useCallback(
        (row: AccountTableRow): DropdownMenuItem[] => {
            if (HEALTHY_OUTCOMES.has(row.outcome)) return [];
            return [
                {
                    key: 'retry',
                    label: t('console.appDetail.accounts.retryRowAction'),
                    onSelect: () => retryMutation.mutate([row]),
                },
            ];
        },
        // `retryMutation` is declared below and is referentially stable for the
        // component's life (useMutation's own contract), so it is read here
        // without being listed as a dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t]
    );

    const retryMutation = useMutation({
        // EAA-1888: scoped to the CURRENT PAGE's rows only — see this
        // component's own docstring for why the pre-1888 app-wide sweep
        // (every failing row across every org) is not reproduced here.
        mutationFn: async (targets: AccountTableRow[]) => {
            const byOrg = new Map<string, string[]>();
            for (const row of targets) {
                const ids = byOrg.get(row.organization.id) ?? [];
                ids.push(row.assignment_id);
                byOrg.set(row.organization.id, ids);
            }
            const results = await Promise.all(
                Array.from(byOrg.entries()).map(([orgId, assignment_ids]) =>
                    retryProvisioning(config.apiBase, accessToken as string, orgId, { assignment_ids })
                )
            );
            return results.reduce((sum, r) => sum + r.requeued, 0);
        },
        onSuccess: (requeued) => {
            setSelectedKeys([]);
            queryClient.invalidateQueries({ queryKey: ['app-accounts'] });
            toast.success(t('console.appDetail.accounts.retrySuccess', { count: requeued }));
        },
        onError: () => toast.error(t('console.appDetail.accounts.retryError')),
    });

    const retryCount = retryTargets.length;

    const columns: ColumnConfig<AccountTableRow>[] = useMemo(
        () => [
            {
                key: 'displayName',
                title: t('console.appDetail.accounts.columnAccount'),
                // EAA-1888: NOT sortable — a display name is joined from the
                // realm snapshot cache, not a DB column, so a real server sort
                // by it would mean loading every row into memory first, the
                // exact anti-pattern this ticket removes (see
                // `AssignmentService.outcomes_for_app`'s own docstring).
                // EAA-1878: the same avatar-initials identity cell the realm and
                // org account tables use, so one person looks identical wherever
                // they are listed. No `onClick`: there is no per-assignment detail
                // read to open (the same reason `ScopedAccountsTable` passes no
                // `onViewDetail`), and a button that goes nowhere is worse than
                // plain text.
                render: (_value, row) => (
                    <IdentityCell
                        primary={row.username || row.displayName}
                        secondary={row.user_email}
                    />
                ),
            },
            {
                key: 'organization',
                title: t('console.appDetail.accounts.columnOrgScope'),
                render: (_value, row) =>
                    row.transfer_from_organization ? (
                        <div className="app-detail-accounts-cell">
                            <strong>
                                {row.transfer_from_organization.name}
                                <span aria-hidden="true"> → </span>
                                {row.organization.name}
                            </strong>
                            <span className="app-detail-hint app-detail-accounts-transferring">
                                {t('console.appDetail.accounts.transferring')}
                            </span>
                        </div>
                    ) : (
                        <div className="app-detail-accounts-cell">
                            <strong>{row.organization.name}</strong>
                            <span className="app-detail-hint">{row.organization.code}</span>
                        </div>
                    ),
            },
            {
                key: 'role',
                title: t('console.appDetail.accounts.columnRole'),
                render: (_value, row) =>
                    // EAA-1861 / EAA-1878: the placeholder Level sentinel (see
                    // `isUnassignedRoleKey`) — showing the raw code→arrow layout
                    // for it would leak an internal implementation detail as if it
                    // were a real, if unmapped, standard role.
                    isUnassignedRoleKey(row.role_key) ? (
                        <span className="app-detail-hint">
                            {t('console.appDetail.accounts.noRoleAssigned')}
                        </span>
                    ) : (
                        <div className="app-detail-chip-wrap">
                            <code>{row.role_key}</code>
                            <span aria-hidden="true">→</span>
                            {row.appRoleLabel || t('console.appDetail.accounts.noAppRole')}
                        </div>
                    ),
            },
            {
                key: 'outcome',
                title: t('console.appDetail.accounts.columnOutcome'),
                render: (_value, row) => (
                    <div className="app-detail-accounts-cell">
                        <StatusChip
                            tone={OUTCOME_CELL_TONE[row.outcome] ?? 'off'}
                            label={(outcomeConfig[row.outcome]?.label as string) ?? row.outcome}
                        />
                        <span className="app-detail-hint">{row.outcome}</span>
                    </div>
                ),
            },
            {
                key: 'outcome_at',
                title: t('console.appDetail.accounts.columnTimestamp'),
                sorter: true,
                // EAA-1888: the real DB column is `last_outcome_at` — the
                // response's per-row field stays `outcome_at` (unchanged, see
                // `ProvisioningOutcomeRow`), so the sort key must be named
                // explicitly rather than defaulting to this column's own key.
                sortKey: 'last_outcome_at',
                render: (_value, row) =>
                    row.outcome_at ? new Date(row.outcome_at).toLocaleString('vi-VN') : '—',
            },
            {
                key: 'sync_attempts',
                title: t('console.appDetail.accounts.columnRetryCount'),
                dataIndex: 'sync_attempts',
                align: 'right',
                sorter: true,
                render: (_value, row) => (
                    <span className={row.sync_attempts > 1 ? 'app-detail-accounts-attempts--high' : undefined}>
                        {row.sync_attempts}
                    </span>
                ),
            },
        ],
        [t, outcomeConfig]
    );

    const app = vm.app;
    if (!app) return null;

    if (vm.accountsError) {
        return (
            <SectionCard>
                <ErrorState description={t('console.appDetail.accounts.loadError')} />
            </SectionCard>
        );
    }

    return (
        // EAA-1840: NOT also `.fit-grow` — `AppDetailPage.tsx`'s own
        // `.app-detail-tab-panel` already carries that class for every tab.
        // Doubling it here made the fit-to-shell global rule
        // (`.portal-console-page--fit .fit-grow > *`) match a SECOND time
        // against this div's own children, turning the plain footer `<p>`
        // below into a flex-growing box that silently ate most of the
        // available height instead of the table.
        <div className="app-detail-accounts-tab">
            <div className="apps-toolbar">
                <Input
                    className="apps-toolbar__search"
                    value={searchValue}
                    onChange={handleSearchChange}
                    placeholder={t('console.appDetail.accounts.searchPlaceholder')}
                    aria-label={t('console.appDetail.accounts.searchPlaceholder')}
                    prefix={
                        <svg
                            viewBox="0 0 24 24"
                            width="15"
                            height="15"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={1.8}
                            aria-hidden="true"
                        >
                            <circle cx="11" cy="11" r="7" />
                            <path d="m20 20-3.5-3.5" strokeLinecap="round" />
                        </svg>
                    }
                />

                <AppsFilterButton
                    label={t('console.appDetail.accounts.columnOutcome')}
                    icon="chevron-trailing"
                    active={vm.accountsOutcomeFilter !== null}
                    value={
                        vm.accountsOutcomeFilter
                            ? (outcomeConfig[vm.accountsOutcomeFilter].label as string)
                            : t('console.appDetail.accounts.allOption')
                    }
                    count={vm.accountsOutcomeFilter ? outcomeCounts[vm.accountsOutcomeFilter] : allRowsTotal}
                    open={openFilter === 'outcome'}
                    onOpenChange={(open) => setOpenFilter(open ? 'outcome' : null)}
                >
                    <div className="apps-menu" style={{ maxHeight: 320, overflowY: 'auto' }}>
                        <span className="apps-menu__label">{t('console.appDetail.accounts.columnOutcome')}</span>
                        <AppsMenuOption
                            checked={vm.accountsOutcomeFilter === null}
                            label={t('console.appDetail.accounts.allOption')}
                            count={allRowsTotal}
                            onSelect={() => {
                                vm.setAccountsOutcomeFilter(null);
                                setOpenFilter(null);
                            }}
                        />
                        {(Object.keys(outcomeConfig) as ProvisioningOutcome[]).map((key) => (
                            <AppsMenuOption
                                key={key}
                                checked={vm.accountsOutcomeFilter === key}
                                label={outcomeConfig[key].label as string}
                                count={outcomeCounts[key] ?? 0}
                                onSelect={() => {
                                    vm.setAccountsOutcomeFilter(key);
                                    setOpenFilter(null);
                                }}
                            />
                        ))}
                    </div>
                </AppsFilterButton>

                <AppsFilterButton
                    label={t('console.appDetail.accounts.columnOrgScope')}
                    icon="chevron-trailing"
                    active={vm.accountsOrgFilter !== null}
                    value={
                        vm.accountsOrgFilter
                            ? orgOptions.find((o) => o.id === vm.accountsOrgFilter)?.name
                            : t('console.appDetail.accounts.allOption')
                    }
                    count={
                        vm.accountsOrgFilter
                            ? orgOptions.find((o) => o.id === vm.accountsOrgFilter)?.count
                            : allRowsTotal
                    }
                    open={openFilter === 'org'}
                    onOpenChange={(open) => setOpenFilter(open ? 'org' : null)}
                >
                    <div className="apps-menu" style={{ maxHeight: 320, overflowY: 'auto' }}>
                        <span className="apps-menu__label">{t('console.appDetail.accounts.columnOrgScope')}</span>
                        <AppsMenuOption
                            checked={vm.accountsOrgFilter === null}
                            label={t('console.appDetail.accounts.allOption')}
                            count={allRowsTotal}
                            onSelect={() => {
                                vm.setAccountsOrgFilter(null);
                                setOpenFilter(null);
                            }}
                        />
                        {orgOptions.map((org) => (
                            <AppsMenuOption
                                key={org.id}
                                checked={vm.accountsOrgFilter === org.id}
                                label={org.name}
                                count={org.count}
                                onSelect={() => {
                                    vm.setAccountsOrgFilter(org.id);
                                    setOpenFilter(null);
                                }}
                            />
                        ))}
                    </div>
                </AppsFilterButton>

                <AppsFilterButton
                    label={t('console.appDetail.accounts.columnRole')}
                    icon="chevron-trailing"
                    active={vm.accountsRoleFilter !== null}
                    value={
                        vm.accountsRoleFilter
                            ? roleOptions.find((r) => r.key === vm.accountsRoleFilter)?.label
                            : t('console.appDetail.accounts.allOption')
                    }
                    count={
                        vm.accountsRoleFilter
                            ? roleOptions.find((r) => r.key === vm.accountsRoleFilter)?.count
                            : allRowsTotal
                    }
                    open={openFilter === 'role'}
                    onOpenChange={(open) => setOpenFilter(open ? 'role' : null)}
                >
                    <div className="apps-menu" style={{ maxHeight: 320, overflowY: 'auto' }}>
                        <span className="apps-menu__label">{t('console.appDetail.accounts.columnRole')}</span>
                        <AppsMenuOption
                            checked={vm.accountsRoleFilter === null}
                            label={t('console.appDetail.accounts.allOption')}
                            count={allRowsTotal}
                            onSelect={() => {
                                vm.setAccountsRoleFilter(null);
                                setOpenFilter(null);
                            }}
                        />
                        {roleOptions.map((role) => (
                            <AppsMenuOption
                                key={role.key}
                                checked={vm.accountsRoleFilter === role.key}
                                label={role.label}
                                count={role.count}
                                onSelect={() => {
                                    vm.setAccountsRoleFilter(role.key);
                                    setOpenFilter(null);
                                }}
                            />
                        ))}
                    </div>
                </AppsFilterButton>

                {filtersActive && (
                    <button type="button" className="apps-toolbar__clear" onClick={clearFilters}>
                        <svg
                            viewBox="0 0 24 24"
                            width="14"
                            height="14"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            aria-hidden="true"
                        >
                            <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
                        </svg>
                        {t('console.appDetail.accounts.clearFilters')}
                    </button>
                )}

                <span className="apps-toolbar__spacer" />
                <span className="apps-toolbar__count">
                    {t('console.appDetail.accounts.showingCount', {
                        shown: vm.accountsTotal,
                        total: allRowsTotal,
                    })}
                </span>
            </div>

            {/* EAA-1840: the frame's quick-filter chip row — one click on an outcome
                toggles the same `outcomeFilter` state the "Kết quả" dropdown drives,
                so the two never disagree about what is currently applied. */}
            <div className="app-detail-accounts-chip-row">
                {QUICK_FILTER_OUTCOMES.map((key) => (
                    <button
                        key={key}
                        type="button"
                        className={
                            vm.accountsOutcomeFilter === key
                                ? 'app-detail-accounts-chip is-active'
                                : 'app-detail-accounts-chip'
                        }
                        onClick={() =>
                            vm.setAccountsOutcomeFilter(vm.accountsOutcomeFilter === key ? null : key)
                        }
                    >
                        {outcomeConfig[key].label as string}
                        <span className="app-detail-accounts-chip__count">{outcomeCounts[key] ?? 0}</span>
                    </button>
                ))}
                <span className="apps-toolbar__spacer" />
                <Button
                    onClick={() => retryMutation.mutate(retryTargets)}
                    disabled={retryCount === 0 || retryMutation.isPending}
                >
                    {retryMutation.isPending
                        ? t('console.appDetail.accounts.retryPending')
                        : t('console.appDetail.accounts.retryButton', { count: retryCount })}
                </Button>
                {onSyncUsers && (
                    <Button onClick={onSyncUsers}>{t('console.appDetail.accounts.pullUsersButton')}</Button>
                )}
            </div>

            <SectionCard bodyPadded={false} className="app-detail-accounts-card">
                {vm.accountsTotal === 0 && !vm.accountsLoading ? (
                    <EmptyState
                        title={t('console.appDetail.accounts.emptyTitle')}
                        description={
                            filtersActive
                                ? t('console.appDetail.accounts.emptyFilteredDescription')
                                : t('console.appDetail.accounts.emptyDescription')
                        }
                        action={
                            !filtersActive && onSyncUsers ? (
                                <Button onClick={onSyncUsers}>
                                    {t('console.appDetail.accounts.pullUsersButton')}
                                </Button>
                            ) : undefined
                        }
                    />
                ) : (
                    <>
                        <SharedAccountsTable<AccountTableRow>
                            rows={rows}
                            columns={columns}
                            rowKey="assignment_id"
                            selectedKeys={selectedKeys}
                            onSelectedKeysChange={setSelectedKeys}
                            onSortChange={vm.setAccountsOrdering}
                            rowActions={rowActions}
                            rowActionsAriaLabel={(row) =>
                                t('console.appDetail.accounts.rowActionsLabel', {
                                    name: row.displayName || row.user_email,
                                })
                            }
                            emptyText={t('console.appDetail.accounts.emptyTitle')}
                        />
                        <AccountsPagination
                            page={vm.accountsPage}
                            totalPages={vm.accountsTotalPages}
                            pageSize={vm.accountsPageSize}
                            onPageSizeChange={vm.setAccountsPageSize}
                            rangeFrom={rangeFrom}
                            rangeTo={rangeTo}
                            total={vm.accountsTotal}
                            onGoTo={vm.setAccountsPage}
                        />
                    </>
                )}
            </SectionCard>
            <p className="app-detail-accounts-footer-note">
                {t('console.appDetail.accounts.footerSummary', {
                    total: allRowsTotal,
                    appName: app.name,
                    needsAttention: vm.accountNeedsAttention,
                })}
            </p>
        </div>
    );
}

export default AppDetailAccountsTab;
